import { checkDefinedOrThrow, ResourceNotFoundError } from '@meterup/common';
import { useMemo } from 'react';

import { ALL_NETWORKS_SLUG, paths } from '../constants';
import { Nav } from '../nav';
import { useRegionNameContext } from '../providers/RegionProvider';
import { useCurrentCompanyFromPathOrNull } from './useCurrentCompanyFromPath';
import { type Network, useNetworksForCompany } from './useNetworksForCompany';

export type { Network };

export function useCompanyAndNetworkSlugsFromPath() {
  const rootParams = Nav.useRegionParams('root', paths.pages.IndividualNetworkRootPage);
  const { regionName } = useRegionNameContext();
  // @ts-ignore: Overloads for this type is weird, RegionName is all valid
  const regionParams = Nav.useRegionParams(regionName, paths.pages.IndividualNetworkRootPage);

  const params = regionParams ?? rootParams;

  if (params?.networkSlug && params?.companyName) {
    return {
      networkSlug: params.networkSlug,
      companySlug: params.companyName,
    };
  }
  return null;
}

export function useNetworkSlugFromPathOrNull() {
  const slugs = useCompanyAndNetworkSlugsFromPath();
  return slugs?.networkSlug ?? null;
}

export function useNetworkSlugFromPath() {
  return checkDefinedOrThrow(
    useNetworkSlugFromPathOrNull(),
    new ResourceNotFoundError('Could not find network slug in path'),
  );
}

export function useNetworkUUIDFromPathOrNull() {
  const params = Nav.useRegionParams('root', paths.pages.NetworkRootPage);
  return params?.networkUUID ?? null;
}

export function useNetworkOrNull(): Network | null {
  const company = useCurrentCompanyFromPathOrNull();
  const networkSlug = useNetworkSlugFromPathOrNull();
  const networkUUID = useNetworkUUIDFromPathOrNull();
  const networks = useNetworksForCompany(company?.company_slug);

  return useMemo(() => {
    if (networkSlug) {
      if (networkSlug === ALL_NETWORKS_SLUG) {
        return null;
      }

      return (
        networks.find((network) => network.slug === networkSlug || network.UUID === networkSlug) ??
        null
      );
    }

    if (networkUUID) {
      return networks.find((network) => network.UUID === networkUUID) ?? null;
    }

    return null;
  }, [networks, networkSlug, networkUUID]);
}

export function useNetwork(): Network {
  return checkDefinedOrThrow(
    useNetworkOrNull(),
    new ResourceNotFoundError('Could not find network'),
  );
}

export function useNetworkUUID(): string | null {
  const network = useNetworkOrNull();
  return network?.UUID ?? null;
}
